import { Control } from "react-hook-form";
import { useGetCountries } from "../../../../api/static-data";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormTextarea } from "../../../../components/ui/Input/FormTextArea/FormTextarea";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Label } from "../../../../components/ui/common/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";

interface LocationCardProps {
  control: Control<any>;
  country: number;
  onCountryChange: (country: number) => void;
}

const EYEBROW_CLASS =
  "tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground tw-font-medium";

/**
 * "Location" card — Address (tall textarea) beside Country (Select) then
 * Province/Zip Code. Country is kept as plain component state (not an RHF
 * field), same as the legacy `SuppllierRegisterForm` — the value/options
 * mapping (`c.id`/`c.country`) is ported unchanged from there. Shared
 * between the supplier Edit drawer and the Ingredient register flow's
 * inline "new supplier" form.
 */
const LocationCard = ({ control, country, onCountryChange }: LocationCardProps) => {
  const { data: countriesData, isLoading: isCountriesLoading } = useGetCountries();

  const countries = (countriesData as unknown as { id: number; country: string }[] | undefined)?.map(
    c => ({ key: c.id, text: c.country, value: c.id })
  );

  return (
    <Card className="tw-gap-4 tw-p-5">
      <CardHeader className="tw-p-0">
        <span className={EYEBROW_CLASS}>Location</span>
      </CardHeader>
      <CardContent className="tw-grid tw-grid-cols-1 tw-gap-4 tw-p-0 sm:tw-grid-cols-2">
        <FormTextarea
          control={control}
          name="supplier_address"
          label="Address"
          placeholder="Address"
          className="tw-min-h-[132px] tw-resize-y tw-text-sm"
          labelClassName="tw-text-xs"
        />

        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <Label className="tw-text-xs">Country</Label>
            <Select
              value={country ? String(country) : undefined}
              onValueChange={value => onCountryChange(Number(value))}
            >
              <SelectTrigger className="!tw-h-9 tw-w-full tw-text-sm">
                <SelectValue placeholder={isCountriesLoading ? "Loading..." : "Select country"} />
              </SelectTrigger>
              <SelectContent>
                {countries?.map(c => (
                  <SelectItem key={c.key} value={String(c.value)}>
                    {c.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            <FormInput
              control={control}
              name="supplier_province"
              label="Province"
              placeholder="Province"
              className="tw-h-9 tw-text-sm"
              labelClassName="tw-text-xs"
            />
            <FormInput
              control={control}
              name="supplier_postal_code"
              label="Zip Code"
              placeholder="Zip Code"
              className="tw-h-9 tw-text-sm tw-font-mono"
              labelClassName="tw-text-xs"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LocationCard;
